import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Receipt, ViewQuilt } from '@mui/icons-material'
import { Box, Button, Divider, IconButton, Tooltip, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetQuoteItemsQuery } from '@/app/services/quoteItems'
import { selectQuote, useGetQuoteQuery } from '@/app/services/quotes'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import { selectedQuoteStatus, selectIsCalculatingQuote } from '@/app/slices/quoteItemsSlice'
import { getNestingMethodLabel } from '@/common/helpers/MaterialConsumptionModes'
import { useNestViewAccess, useNumberFormatter } from '@/common/hooks'
import { QUOTE_STATUS_ARRAY } from '@/common/utils'
import PriceDetailsDialog from '@/features/quotes/components/PriceDetailsDialog/PriceDetailsDialog'
import QuoteCalculatorNestDialog from '@/features/quotes/components/QuoteCalculatorNestDialog/QuoteCalculatorNestDialog'

const classes = {
    timesSection: {
        width: '250px',
        gap: '12px',
    },
    pricePercentage: {
        fontSize: '14px',
        color: 'text.secondary',
    },
    bold: {
        fontWeight: 'bold',
    },
    itemColumn: {
        display: 'flex',
        flex: 1,
    },
    alignRight: {
        justifyContent: 'flex-end',
    },
    inputColumn: {
        pl: 2,
    },
    ItemDetails_Calculating: {
        gap: '12px',
    },
    clickable: {
        cursor: 'pointer',
    },
    unstyledButton: {
        fontSize: '15px',
        fontWeight: '500',
        lineHeight: '24px',
    },
}

const ItemRow = ({ children }) => (
    <Box
        sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
        }}
    >
        {children}
    </Box>
)

const ItemTimes = ({ quoteItemId }) => {
    const { quoteId } = useParams()
    const organisationId = useSelector(selectOrganisationId)
    const isCalculatingQuote = useSelector(selectIsCalculatingQuote)
    const hideTimes = useSelector(selectedQuoteStatus) === QUOTE_STATUS_ARRAY.NotCalculated

    const hasNestViewAccess = useNestViewAccess()

    const selectedQuote = useSelector((state) => selectQuote(state, { organisationId: organisationId, quoteId }))
    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    const { isFetching: isFetchingQuote } = useGetQuoteQuery({
        organisationId,
        quoteId,
    })

    const [openNest, setOpenNest] = useState(false)
    const handleClickOpenNest = () => {
        setOpenNest(true)
    }
    const handleCloseNest = () => {
        setOpenNest(false)
    }

    const [priceDetailsData, setPriceDetailsData] = useState(null)
    const displayPriceDetails = (quoteItem) => {
        setPriceDetailsData(quoteItem)
    }

    const hidePriceDetails = () => {
        setPriceDetailsData(null)
    }

    const { t } = useTranslation()
    const { n } = useNumberFormatter({
        numberOfDecimalPlaces: 0,
    })

    const isRotary = Boolean(quoteItem?.profile)
    const organisation = useSelector(selectOrganisation)
    const nestingMethodLabel = t(
        getNestingMethodLabel({
            isRotary,
            materialConsumptionMode: quoteItem?.materialConsumptionMode,
            nestingAlgorithmToUse: organisation?.nestingAlgorithmToUse,
            quantity: quoteItem?.quantity,
            rectangularNestingForSingularParts: organisation?.rectangularNestingForSingularParts,
        })
    )
    const nestTooltipTitle = !quoteItem?.nestImageUri
        ? `${nestingMethodLabel} — ${t('no nest image available')}`
        : !hasNestViewAccess
          ? t('Nest view access required')
          : `${t('View nest')} (${nestingMethodLabel})`

    return (
        <>
            <Box
                sx={[
                    {
                        alignItems: 'flex-start',
                        display: 'flex',
                        flex: '1 0 auto',
                        flexDirection: 'column',
                        justifyContent: 'space-around',
                    },
                    classes.timesSection,
                ]}
            >
                <ItemRow>
                    <Typography
                        data-testid="quote-item-times-section-title"
                        sx={classes.bold}
                    >
                        {t('Unit times')}
                    </Typography>
                    <Button
                        data-testid="nqp-quote-item-unit-times-details"
                        endIcon={<Receipt />}
                        size="small"
                        variant="text"
                        onClick={() => displayPriceDetails(quoteItem)}
                    >
                        {t('Details')}
                    </Button>
                </ItemRow>

                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-total-cutting-time-label"
                        sx={classes.itemColumn}
                    >
                        {t('Total cutting')}
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.alignRight]}>
                        {hideTimes || isCalculatingQuote || isFetchingQuote
                            ? String.fromCharCode(8212)
                            : n(
                                  (quoteItem?.setupTimePerItemSeconds ?? 0) +
                                      (quoteItem?.sheetChangeTimePerItemSeconds ?? 0) +
                                      (quoteItem?.runtime ?? 0) +
                                      (quoteItem?.unloadTimePerItemSeconds ?? 0)
                              ) + 's'}
                    </Typography>
                </ItemRow>
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-total-folding-time-label"
                        sx={classes.itemColumn}
                    >
                        {t('Total folding')}
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.alignRight]}>
                        {hideTimes ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        quoteItem?.foldingTotalTimePerItemSeconds <= 0
                            ? String.fromCharCode(8212)
                            : n(quoteItem?.foldingTotalTimePerItemSeconds) + 's'}
                    </Typography>
                </ItemRow>
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-secondary-processes-time-label"
                        sx={classes.itemColumn}
                    >
                        {t('Secondary processes')}
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.alignRight]}>
                        {hideTimes ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        quoteItem?.secondaryProcessTotalTimePerItemSeconds <= 0
                            ? String.fromCharCode(8212)
                            : n(quoteItem?.secondaryProcessTotalTimePerItemSeconds) + 's'}
                    </Typography>
                </ItemRow>
                <Divider
                    orientation="horizontal"
                    flexItem
                />
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-time-per-unit-label"
                        sx={[classes.itemColumn, classes.bold]}
                    >
                        {t('Time per unit')}
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.alignRight, classes.bold]}>
                        {hideTimes || isCalculatingQuote || isFetchingQuote
                            ? String.fromCharCode(8212)
                            : n(
                                  (quoteItem?.setupTimePerItemSeconds ?? 0) +
                                      (quoteItem?.sheetChangeTimePerItemSeconds ?? 0) +
                                      (quoteItem?.runtime ?? 0) +
                                      (quoteItem?.unloadTimePerItemSeconds ?? 0) +
                                      (quoteItem?.foldingTotalTimePerItemSeconds ?? 0) +
                                      (quoteItem?.secondaryProcessTotalTimePerItemSeconds ?? 0)
                              ) + 's'}
                    </Typography>
                </ItemRow>
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-units-per-sheet-label"
                        sx={classes.bold}
                    >
                        {t('Units per sheet')}
                    </Typography>
                    <Box sx={[classes.itemColumn, classes.alignRight]}>
                        {!hideTimes && (quoteItem?.partsPerSheet || quoteItem?.partsPerSheet !== 0) ? (
                            <Tooltip title={nestTooltipTitle}>
                                <span>
                                    <IconButton
                                        aria-label="viewNest"
                                        color="primary"
                                        data-testid="nqp-quote-item-view-nest"
                                        disabled={!quoteItem?.nestImageUri || !hasNestViewAccess}
                                        size="small"
                                        onClick={handleClickOpenNest}
                                    >
                                        <ViewQuilt />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : (
                            ''
                        )}
                    </Box>
                    <Typography sx={classes.bold}>{!hideTimes ? quoteItem?.partsPerSheet : null}</Typography>
                </ItemRow>
            </Box>
            <QuoteCalculatorNestDialog
                handleCloseNest={handleCloseNest}
                isRotary={isRotary}
                materialConsumptionMode={quoteItem?.materialConsumptionMode}
                nestImageUri={quoteItem?.nestImageUri}
                nestingAlgorithmToUse={organisation?.nestingAlgorithmToUse}
                openNest={openNest}
                organisationNestingEnabled={hasNestViewAccess}
                quantity={quoteItem?.quantity}
                rectangularNestingForSingularParts={organisation?.rectangularNestingForSingularParts}
                title={quoteItem?.name}
            />
            <PriceDetailsDialog
                open={!!priceDetailsData}
                quoteItem={priceDetailsData}
                quoteOverrides={selectedQuote?.quoteOverrides}
                taxRate={selectedQuote?.taxRate}
                onClose={hidePriceDetails}
            />
        </>
    )
}
ItemTimes.propTypes = {
    quoteItemId: PropTypes.string,
}
export default memo(ItemTimes)
